/**
 * openDesignTableFromDdlCard — Design-table hand-off for the DDL summary
 * card's "Design table" action (§2.9 S13, plan-9 decision 3). Store side
 * effects only, no React — mirrors the store-mocking pattern used by
 * `visual-ddl-editor/dependents/openInDependencyViewer.ts`.
 *
 * Two modes, driven by `DdlCardModel.designTable.mode`:
 *  - `'create'` — the statement's table doesn't exist yet (a CREATE TABLE
 *    the user is still writing), so the hand-off opens a Table Designer tab
 *    in create mode SEEDED FROM THE STATEMENT TEXT (`currentSql`, no
 *    `initialSql`): `TableDesignerTab`'s bootstrap effect only fills
 *    `currentSql` when it is `undefined`, so a pre-seeded value is respected
 *    verbatim, and `canSave` treats a no-`initialSql` + non-empty buffer as
 *    saveable — correct create semantics. Truthful: this designs the
 *    STATEMENT, not a table that exists in the database yet.
 *  - `'alter'` — the statement targets an existing table, so the hand-off
 *    delegates straight to the shared `designTableAction.execute` (the same
 *    action Object Explorer's "Design Table…" context-menu item calls),
 *    which synthesizes the buffer from LIVE metadata. This module never
 *    reimplements that synthesis.
 *
 * Both modes resolve the target schema the same way: the qualified name's
 * own schema when the statement had one, otherwise the engine's default
 * schema (`defaultSchemaForEngine`) — alter-mode needs a real schema to look
 * up live metadata, and create-mode needs one to seed the Designer tab's
 * `schema` field.
 */
import { getConnectionById } from '../../../connections/utils/connectionLookup';
import type { UserFacingEngineId } from '../../../../core/engines';
import { defaultSchemaForEngine } from '../../../navigational-features/go-to/externalNavigation';
import { designTableAction } from '../../../shared/object-actions/actions/designActions';
import { useWorkspaceStore } from '../../core/store/workspaceStore';
import type { DdlCardModel } from './compactDdlCard';
import { safeConnectionAlias, safeConnectionServer } from '../../../../types/safeConnectionDisplay';

export interface OpenDesignTableFromDdlCardArgs {
  readonly design: NonNullable<DdlCardModel['designTable']>;
  readonly connectionId: string;
  readonly database: string | null;
}

export interface OpenDesignTableFromDdlCardResult {
  readonly opened: boolean;
  readonly tabId?: string;
}

export function openDesignTableFromDdlCard(
  args: OpenDesignTableFromDdlCardArgs,
): OpenDesignTableFromDdlCardResult {
  const { design, connectionId, database } = args;
  const { activeConnection, connection } = getConnectionById(connectionId);
  if (!activeConnection || !connection) return { opened: false };

  const engine = (activeConnection.connection.engine as UserFacingEngineId) || 'mssql';
  const schema = design.schema ?? defaultSchemaForEngine(engine);
  const databaseName = database ?? '';

  if (design.mode === 'alter') {
    void designTableAction.execute({
      connectionId,
      databaseName,
      schemaName: schema,
      objectName: design.tableName,
      objectType: 'table',
    });
    return { opened: true };
  }

  const tabId = useWorkspaceStore.getState().addTab({
    title: `${design.tableName} [Design]`,
    type: 'table-designer',
    content: {
      mode: 'create',
      tableName: design.tableName,
      schema,
      database: databaseName,
      currentSql: design.statementText,
      isLoading: false,
    },
    connectionInfo: {
      connectionId,
      connectionName: safeConnectionAlias(connection, 'Unnamed Connection'),
      serverHost: safeConnectionServer(activeConnection.connection),
      database: databaseName,
    },
  });

  return { opened: true, tabId };
}
